import React, { useEffect, useMemo, useState } from "react";
import api from "../../../utils/api";

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

const MaterialsTab = ({ workOrderId, canEdit }) => {
  const [materials, setMaterials] = useState([]);
  const [summary, setSummary] = useState({
    total_items: 0,
    total_price: 0,
    billable_total: 0,
  });

  // Billing rule code auto-resolved from settings. never asked from user
  const [ruleMatCode, setRuleMatCode] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);

  const [form, setForm] = useState({
    description: "",
    quantity: 1,
    unit_price: "",
    item_group_code: "",
    is_billable: true,
    notes: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchMaterials = async () => {
    try {
      const [materialsRes, summaryRes] = await Promise.all([
        api.get(`/workorder-materials/${workOrderId}`),
        api.get(`/workorder-materials-summary/${workOrderId}`),
      ]);

      setMaterials(materialsRes.data || []);
      setSummary(
        summaryRes.data || {
          total_items: 0,
          total_price: 0,
          billable_total: 0,
        }
      );
    } catch (err) {
      console.error("Failed to fetch materials:", err);
      showSnackbar("Failed to load materials.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch the active billing rule for this WO so we can auto-populate
  // item_group_code without asking the user every time.
  const fetchBillingRuleCode = async () => {
    try {
      const res = await api.get(`/work-orders/${workOrderId}/billing-rule-codes`);
      setRuleMatCode(res.data?.materials_billing_code ?? null);
    } catch {
      setRuleMatCode(null);
    }
  };

  useEffect(() => {
    if (workOrderId) {
      fetchMaterials();
      fetchBillingRuleCode();
    }
  }, [workOrderId]); // eslint-disable-line

  const resetForm = () => {
    setForm({
      description: "",
      quantity: 1,
      unit_price: "",
      item_group_code: "",
      is_billable: true,
      notes: "",
    });
    setEditingMaterial(null);
  };

  const openCreateDialog = () => {
    resetForm();
    // Pre-populate the billing code from the rule. user never needs to type it
    if (ruleMatCode) {
      setForm(f => ({ ...f, item_group_code: ruleMatCode }));
    }
    setOpenDialog(true);
  };

  const openEditDialog = (material) => {
    setEditingMaterial(material);
    setForm({
      description: material.description || "",
      quantity: material.quantity ?? 1,
      unit_price: material.unit_price ?? "",
      item_group_code: material.item_group_code || "",
      is_billable: Boolean(material.is_billable),
      notes: material.notes || "",
    });
    setOpenDialog(true);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const computedTotal = useMemo(() => {
    const qty = parseFloat(form.quantity);
    const price = parseFloat(form.unit_price);
    if (!isNaN(qty) && !isNaN(price) && qty > 0 && price >= 0) {
      return (qty * price).toFixed(2);
    }
    return "";
  }, [form.quantity, form.unit_price]);

  const validateForm = () => {
    if (!form.description?.trim()) {
      showSnackbar("Please enter a description.", "error");
      return false;
    }

    if (!form.quantity || Number(form.quantity) <= 0) {
      showSnackbar("Quantity must be greater than 0.", "error");
      return false;
    }

    if (form.unit_price === "" || form.unit_price === null || Number(form.unit_price) < 0) {
      showSnackbar("Please enter a valid unit price.", "error");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        work_order_id: Number(workOrderId),
        description: form.description.trim(),
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
        item_group_code: form.item_group_code?.trim() || null,
        is_billable: Boolean(form.is_billable),
        notes: form.notes?.trim() || null,
      };

      if (editingMaterial?.material_id) {
        await api.put(`/workorder-materials/${editingMaterial.material_id}`, payload);
        showSnackbar("Material updated successfully.");
      } else {
        await api.post("/workorder-materials", payload);
        showSnackbar("Material added successfully.");
      }

      setOpenDialog(false);
      resetForm();
      fetchMaterials();
    } catch (err) {
      console.error("Failed to save material:", err);
      showSnackbar("Failed to save material.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (materialId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this material entry?"
    );
    if (!confirmed) return;

    try {
      await api.delete(`/workorder-materials/${materialId}`);
      showSnackbar("Material deleted successfully.");
      fetchMaterials();
    } catch (err) {
      console.error("Failed to delete material:", err);
      showSnackbar("Failed to delete material.", "error");
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0.00";
    return `$${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "240px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Card
            sx={{
              ...pageCardSx,
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderTop: "4px solid #2f63ad",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
                >
                  Materials & Supplies
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Track materials, parts, and supplies used for this work order along with billing details.
                </Typography>
              </Box>

              {canEdit && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                  sx={primaryButtonSx}
                >
                  Add Material
                </Button>
              )}
            </Box>

            <Grid container spacing={2} sx={{ mt: 1.5 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    p: 2,
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    Total Items
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    {summary.total_items || 0}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    p: 2,
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    Total Value
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    {formatCurrency(summary.total_price || 0)}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    p: 2,
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    Billable Value
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                    {formatCurrency(summary.billable_total || 0)}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ ...pageCardSx, p: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <InventoryOutlinedIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Material Entries
              </Typography>
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            {materials.length === 0 ? (
              <Box
                sx={{
                  border: "1px dashed rgba(47,99,173,0.18)",
                  borderRadius: "16px",
                  px: 3,
                  py: 5,
                  textAlign: "center",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <BuildOutlinedIcon sx={{ fontSize: 38, color: "#8ba3c7", mb: 1 }} />
                <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                  No materials yet
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                  Start tracking parts and supplies used for this work order by adding the first material.
                </Typography>
                {canEdit && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreateDialog}
                    sx={primaryButtonSx}
                  >
                    Add First Material
                  </Button>
                )}
              </Box>
            ) : (
              <Grid container spacing={2}>
                {materials.map((material) => {
                  const lineTotal = (
                    parseFloat(material.quantity || 0) *
                    parseFloat(material.unit_price || 0)
                  ).toFixed(2);

                  return (
                    <Grid item xs={12} key={material.material_id}>
                      <Card
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid rgba(47,99,173,0.08)",
                          boxShadow: "none",
                          p: 2,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "rgba(47,99,173,0.18)",
                            boxShadow: "0 10px 24px rgba(47,99,173,0.08)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", md: "center" },
                            flexDirection: { xs: "column", md: "row" },
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                                mb: 0.75,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, color: "#223142" }}
                              >
                                {material.description}
                              </Typography>

                              <Chip
                                label={material.is_billable ? "Billable" : "Non-Billable"}
                                size="small"
                                color={material.is_billable ? "success" : "default"}
                                variant={material.is_billable ? "filled" : "outlined"}
                              />
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                                mb: material.notes ? 0.75 : 0,
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                                  Qty:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                                  {material.quantity}
                                </Typography>
                              </Box>

                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                                  Unit Price:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                                  {formatCurrency(material.unit_price)}
                                </Typography>
                              </Box>

                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                                  Total:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700, color: "#2f63ad" }}
                                >
                                  {formatCurrency(lineTotal)}
                                </Typography>
                              </Box>

                              {material.item_group_code && (
                                <Chip
                                  label={material.item_group_code}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    backgroundColor: "rgba(47,99,173,0.10)",
                                    color: "#2f63ad",
                                    fontFamily: "monospace",
                                  }}
                                />
                              )}
                            </Box>

                            {material.notes && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6b778c",
                                  whiteSpace: "pre-wrap",
                                  lineHeight: 1.65,
                                  mt: 0.5,
                                }}
                              >
                                {material.notes}
                              </Typography>
                            )}
                          </Box>

                          {canEdit && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignSelf: { xs: "flex-end", md: "flex-start" },
                              }}
                            >
                              <IconButton
                                onClick={() => openEditDialog(material)}
                                sx={{
                                  border: "1px solid rgba(47,99,173,0.14)",
                                  borderRadius: "12px",
                                  color: "#2f63ad",
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                onClick={() => handleDelete(material.material_id)}
                                sx={{
                                  border: "1px solid rgba(220,0,78,0.14)",
                                  borderRadius: "12px",
                                  color: "#d32f2f",
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
          {editingMaterial ? "Edit Material" : "Add Material"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                required
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                sx={textFieldSx}
                placeholder="e.g. PVC pipe 1/2 inch, Paint. interior white, etc."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                required
                value={form.quantity}
                onChange={(e) => handleChange("quantity", e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Unit Price ($)"
                type="number"
                fullWidth
                required
                value={form.unit_price}
                onChange={(e) => handleChange("unit_price", e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Total"
                fullWidth
                value={computedTotal ? `$${computedTotal}` : ""}
                InputProps={{ readOnly: true }}
                sx={{
                  ...textFieldSx,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(47,99,173,0.04)",
                    borderRadius: "10px",
                  },
                }}
                helperText="Auto-calculated from quantity × unit price."
              />
            </Grid>

            {/* item_group_code is auto-populated from the billing rule. not shown to user */}

            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={3}
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                sx={textFieldSx}
                placeholder="Any additional notes about this material..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_billable}
                    onChange={(e) => handleChange("is_billable", e.target.checked)}
                  />
                }
                label="Billable"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={primaryButtonSx}
          >
            {saving
              ? "Saving..."
              : editingMaterial
              ? "Update Material"
              : "Save Material"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaterialsTab;
